import React, {useEffect, useMemo, useState} from "react";
import {useNavigate, useOutletContext, useParams, useSearchParams} from "react-router-dom";
import Button from "../../../../../components/Button";
import api from "../../../../../lib/api";
import {notify} from "../../../../../components/Notifications";
import {getErrMsg} from "../../../../../lib/errors";
import {useBusiness} from "../../../../../context/BusinessContext";
import {SaveIcon} from "lucide-react";
import SectionHeading from "../../../../../components/SectionHeading";

type OutletCtx = { eventId: string | null };

type RefundForm = {
    allowed: boolean;
    rate: string;          // percent UI string
    deadlineDays: string;  // UI string
    description: string;
};

const cx = (...c: Array<string | false | null | undefined>) => c.filter(Boolean).join(" ");

function toNumber(v: string): number {
    const n = Number((v ?? "").replace(/[^\d.]/g, ""));
    return Number.isFinite(n) ? n : 0;
}

function toInt(v: string): number {
    const n = Number((v ?? "").replace(/[^\d]/g, ""));
    return Number.isFinite(n) ? Math.max(0, Math.trunc(n)) : 0;
}

export function RefundPolicyStep({mode}: { mode: "create" | "edit" }) {
    const {activeBusiness} = useBusiness();
    const businessId = activeBusiness?.id ?? null;

    const navigate = useNavigate();
    const params = useParams();
    const [sp] = useSearchParams();
    const outlet = useOutletContext<OutletCtx>();

    const eventId =
        mode === "edit"
            ? (params.eventId ?? outlet.eventId ?? null)
            : (outlet.eventId ?? sp.get("eventId") ?? null);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [form, setForm] = useState<RefundForm>({
        allowed: false,
        rate: "0",
        deadlineDays: "0",
        description: "",
    });

    useEffect(() => {
        if (!businessId || !eventId) return;

        setLoading(true);
        (async () => {
            try {
                const {data} = await api.get(`/api/v1/business/${businessId}/events/${eventId}/refund-policy`);
                setForm({
                    allowed: Boolean(data?.allowed ?? false),
                    rate: String(data?.rate ?? 0),
                    deadlineDays: String(data?.deadlineDays ?? 0),
                    description: data?.description ?? "",
                });
            } catch (e) {
                notify.error(getErrMsg(e, "Failed to load refund policy"));
            } finally {
                setLoading(false);
            }
        })();
    }, [businessId, eventId]);

    const rateNum = useMemo(() => Math.min(100, Math.max(0, toNumber(form.rate))), [form.rate]);
    const daysNum = useMemo(() => toInt(form.deadlineDays), [form.deadlineDays]);

    const save = async () => {
        if (!businessId || !eventId) return;

        setSaving(true);
        try {
            await api.put(`/api/v1/business/${businessId}/events/${eventId}/refund-policy`, {
                allowed: form.allowed,
                rate: form.allowed ? rateNum : 0,
                deadlineDays: form.allowed ? daysNum : 0,
                description: form.description ?? "",
            });

            notify.success("Refund policy saved");
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to save refund policy"));
        } finally {
            setSaving(false);
        }
    };

    const goNext = () => {
        if (!eventId) return;
        if (mode === "edit") navigate(`/business/events/${encodeURIComponent(eventId)}/edit/mailing`);
        else navigate(`/business/events/new/mailing?eventId=${encodeURIComponent(eventId)}`);
    };


    if (!eventId) {
        return (
            <div className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-600">
                Please complete <span className="font-medium">Event Details</span> first.
            </div>
        );
    }

    return (
        <div className="space-y-6">

            <SectionHeading
                title={mode === "edit" ? "Edit Refund Policy" : "Set Refund Policy"}
                description={mode === "edit" ? "Update the refund policy for this event." : "Set up a refund policy for your event. You can allow refunds, specify a refund rate, and set a deadline for refund requests."}
                actions={
                    <>
                        <Button type="button" variant="primary" size="md"
                                onClick={save}
                                disabled={saving}>
                            {saving ? "Saving…" :
                                <span className="inline-flex items-center gap-2"><SaveIcon
                                    className="size-4"/>Save</span>}
                        </Button>
                    </>
                }
            />

            {loading ? (
                <div className="py-10 text-sm text-gray-500">Loading refund policy…</div>
            ) : (
                <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                    <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0">
                            <div className="text-sm font-semibold text-gray-900">Allow refunds</div>
                            <p className="mt-1 text-sm text-gray-600">
                                If disabled, all purchases are final (no refunds).
                            </p>
                        </div>

                        <label className="inline-flex items-center gap-3">
                            <input
                                type="checkbox"
                                checked={form.allowed}
                                onChange={(e) => setForm((p) => ({...p, allowed: e.target.checked}))}
                                className="size-5 rounded border-gray-300 text-orange-700 focus:ring-orange-600"
                            />
                            <span
                                className={cx("text-sm font-semibold", form.allowed ? "text-gray-900" : "text-gray-500")}>
                                {form.allowed ? "Enabled" : "Disabled"}
                            </span>
                        </label>
                    </div>

                    <div className={cx("mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2", !form.allowed && "opacity-50")}>
                        <div>
                            <label className="block text-sm font-medium text-gray-900">Refund rate (%)</label>
                            <p className="mt-1 text-xs text-gray-500">How much is refunded (e.g. 100 = full refund)</p>
                            <input
                                disabled={!form.allowed}
                                value={form.rate}
                                onChange={(e) => setForm((p) => ({...p, rate: e.target.value}))}
                                inputMode="decimal"
                                className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                            />
                            <p className="mt-2 text-xs text-gray-500">Current: {rateNum.toFixed(0)}%</p>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-900">Deadline (days before
                                event)</label>
                            <p className="mt-1 text-xs text-gray-500">Last day a customer can request a refund</p>
                            <input
                                disabled={!form.allowed}
                                value={form.deadlineDays}
                                onChange={(e) => setForm((p) => ({...p, deadlineDays: e.target.value}))}
                                inputMode="numeric"
                                className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                            />
                            <p className="mt-2 text-xs text-gray-500">Current: {daysNum} days</p>
                        </div>

                        <div className="sm:col-span-2">
                            <label className="block text-sm font-medium text-gray-900">Refund description
                                (optional)</label>
                            <p className="mt-1 text-xs text-gray-500">
                                Shown to customers (e.g. “Refunds are processed within 5 business days.”)
                            </p>
                            <textarea
                                disabled={!form.allowed}
                                rows={4}
                                value={form.description}
                                onChange={(e) => setForm((p) => ({...p, description: e.target.value}))}
                                className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                            />
                        </div>
                    </div>

                    {!form.allowed ? (
                        <div className="mt-6 rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
                            Refunds are disabled. Customers will see that purchases are final.
                        </div>
                    ) : null}
                </div>
            )}

            <div className="flex items-center justify-end gap-3">
                <Button type="button" variant="secondary" size="md"
                        onClick={() => navigate("/business/events")}>
                    Exit
                </Button>
                <Button type="button" variant="primary" size="md" onClick={goNext} disabled={saving}>
                    Continue to Mailing
                </Button>
            </div>
        </div>
    );
}
